import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Filter, Settings, X } from 'lucide-react';

// ===== STATUS COLOR HELPERS =====
const getStatusBorderColor = (status) => {
  switch (status) {
    case 'confirmed': return 'border-l-emerald-500';
    case 'pending': return 'border-l-gray-400';
    case 'cancelled': return 'border-l-rose-500';
    default: return 'border-l-gray-300';
  }
};

const getStatusBadgeColor = (status) => {
  switch (status) {
    case 'confirmed': return 'bg-emerald-100 text-emerald-700';
    case 'pending': return 'bg-gray-100 text-gray-600';
    case 'cancelled': return 'bg-rose-100 text-rose-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

// Get booking styles based on status (for inline styles)
const getBookingStyles = (status) => {
  const styles = {
    confirmed: {
      bg: '#f0fdf4',
      border: '#10b981',
      opacity: 'opacity-100'
    },
    pending: {
      bg: '#f9fafb',
      border: '#6b7280',
      opacity: 'opacity-70'
    },
    cancelled: {
      bg: '#fef2f2',
      border: '#ef4444',
      opacity: 'opacity-50'
    }
  };
  return styles[status] || styles.confirmed;
};

// ===== MONTH VIEW COMPONENT =====
const MonthView = ({ bookings, currentDate, onBookingClick, onDayClick, onMoreClick }) => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Get first day of month and total days
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Create array of day cells
  const days = [];
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Add empty cells for days before month starts
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  // Add actual days
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  // Get bookings for a specific date
  const getBookingsForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return bookings.filter(b => b.date === dateStr);
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() &&
           month === today.getMonth() &&
           year === today.getFullYear();
  };

  return (
    <div>
      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {weekDays.map(day => (
          <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2 p-1">
        {days.map((day, index) => {
          const dayBookings = day ? getBookingsForDate(day) : [];
          const today = day && isToday(day);
          const visibleBookings = dayBookings.slice(0, 3);
          const remainingCount = dayBookings.length - 3;

          return (
            <button
              key={index}
              onClick={() => {
                if (day) {
                  const targetDate = new Date(year, month, day);
                  onDayClick(targetDate);
                }
              }}
              disabled={!day}
              className={`h-32 border rounded-lg p-2 overflow-hidden text-left transition-all ${
                day ? 'bg-white border-gray-200 hover:border-purple-300 hover:shadow-sm cursor-pointer' : 'bg-gray-50 border-transparent cursor-default'
              } ${today ? 'ring-2 ring-purple-500' : ''}`}
            >
              {day && (
                <>
                  {/* Day number */}
                  <div className={`text-sm font-semibold mb-2 ${
                    today ? 'text-purple-600' : 'text-gray-900'
                  }`}>
                    {day}
                  </div>

                  {/* Bookings - compact list style */}
                  <div className="space-y-0.5" onClick={(e) => e.stopPropagation()}>
                    {visibleBookings.map(booking => {
                      const styles = getBookingStyles(booking.status);

                      return (
                        <button
                          key={booking.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onBookingClick(booking);
                          }}
                          className={`w-full text-left px-1.5 py-1 rounded text-xs transition-all hover:opacity-80 ${styles.opacity}`}
                          style={{
                            backgroundColor: styles.bg,
                            borderLeft: `2px solid ${styles.border}`
                          }}
                        >
                          <div className="flex items-baseline gap-1">
                            <span className={`font-semibold text-gray-900 whitespace-nowrap ${booking.status === 'cancelled' ? 'line-through' : ''}`}>
                              {booking.time}
                            </span>
                            <span className={`text-gray-700 truncate ${booking.status === 'cancelled' ? 'line-through' : ''}`}>
                              {booking.clientName}
                            </span>
                          </div>
                        </button>
                      );
                    })}

                    {remainingCount > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoreClick({
                            date: new Date(year, month, day),
                            bookings: dayBookings
                          });
                        }}
                        className="w-full text-center py-0.5 text-xs text-purple-600 hover:text-purple-700 font-medium"
                      >
                        +{remainingCount} more
                      </button>
                    )}
                  </div>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ===== WEEK VIEW COMPONENT =====
const WeekView = ({ bookings, currentDate, onBookingClick }) => {
  const weekStart = new Date(currentDate);
  weekStart.setDate(currentDate.getDate() - currentDate.getDay());

  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(weekStart);
    day.setDate(weekStart.getDate() + i);
    weekDays.push(day);
  }

  const hours = Array.from({ length: 14 }, (_, i) => i + 8); // 8 AM to 9 PM

  const getBookingsForDate = (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return bookings.filter(b => b.date === dateStr);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Convert time string to hour (0-23)
  const timeToHour = (timeStr) => {
    const timeParts = timeStr.match(/(\d+):?(\d*)?\s*(AM|PM)/i);
    if (!timeParts) return 0;
    let hour = parseInt(timeParts[1]);
    const isPM = timeParts[3].toUpperCase() === 'PM';
    if (isPM && hour !== 12) hour += 12;
    if (!isPM && hour === 12) hour = 0;
    return hour;
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[800px]">
        {/* Day headers */}
        <div className="grid grid-cols-8 gap-1 mb-4 pb-1">
          <div className="text-sm font-semibold text-gray-600"></div>
          {weekDays.map(day => {
            const today = isToday(day);
            return (
              <div
                key={day.toISOString()}
                className="text-center p-1"
              >
                <div className={`inline-block px-3 py-2 rounded-lg ${
                  today ? 'bg-purple-50 ring-2 ring-purple-500' : ''
                }`}>
                  <div className="text-xs text-gray-600">
                    {day.toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                  <div className={`text-lg font-semibold ${today ? 'text-purple-600' : 'text-gray-900'}`}>
                    {day.getDate()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Time grid */}
        <div className="relative">
          {hours.map(hour => (
            <div key={hour} className="grid grid-cols-8 gap-2" style={{ height: '60px' }}>
              <div className="text-sm text-gray-600 text-right pr-2 pt-1">
                {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
              </div>
              {weekDays.map(day => {
                const dayBookings = getBookingsForDate(day);
                const hourBookings = dayBookings.filter(b => timeToHour(b.time) === hour);

                return (
                  <div key={day.toISOString()} className="relative border-t border-gray-100 bg-gray-50">
                    {hourBookings.map(booking => (
                      <button
                        key={booking.id}
                        onClick={() => onBookingClick(booking)}
                        className={`absolute inset-x-1 top-1 bg-white border border-gray-200 border-l-4 rounded px-2 py-1.5 text-left transition-all hover:shadow-sm z-10 ${
                          getStatusBorderColor(booking.status)
                        } ${booking.status === 'cancelled' ? 'opacity-60' : ''}`}
                        style={{
                          height: `${Math.min(booking.duration, 60) - 8}px`
                        }}
                      >
                        <div className={`font-semibold text-gray-900 text-xs truncate ${booking.status === 'cancelled' ? 'line-through' : ''}`}>
                          {booking.time}
                        </div>
                        <div className={`text-gray-600 text-xs truncate ${booking.status === 'cancelled' ? 'line-through' : ''}`}>
                          {booking.clientName}
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ===== DAY VIEW COMPONENT - PROPORTIONAL TIME BLOCKS =====
const DayView = ({ bookings, currentDate, onBookingClick }) => {
  const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
  const dayBookings = bookings.filter(b => b.date === dateStr);

  // Convert time string to minutes since midnight
  const timeToMinutes = (timeStr) => {
    const timeParts = timeStr.match(/(\d+):?(\d*)?\s*(AM|PM)/i);
    if (!timeParts) return 0;
    let hours = parseInt(timeParts[1]);
    const minutes = parseInt(timeParts[2]) || 0;
    const isPM = timeParts[3].toUpperCase() === 'PM';
    if (isPM && hours !== 12) hours += 12;
    if (!isPM && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  // Create array of 15-minute slots from 8 AM to 9 PM
  const slots = [];
  const startHour = 8; // 8 AM
  const endHour = 21; // 9 PM

  for (let hour = startHour; hour <= endHour; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const totalMinutes = hour * 60 + minute;
      const isHourMark = minute === 0;
      const timeLabel = isHourMark
        ? (hour === 12 ? '12:00 PM' : hour > 12 ? `${hour - 12}:00 PM` : `${hour}:00 AM`)
        : '';

      // Find booking that starts in this slot
      const slotBooking = dayBookings.find(b => {
        const bookingStartMinutes = timeToMinutes(b.time);
        return bookingStartMinutes >= totalMinutes && bookingStartMinutes < totalMinutes + 15;
      });

      slots.push({
        time: totalMinutes,
        label: timeLabel,
        isHourMark,
        booking: slotBooking
      });
    }
  }

  return (
    <div className="relative">
      {slots.map((slot, index) => {
        const showBooking = slot.booking;

        // Calculate how many slots this booking occupies (40px per 15-min slot)
        const bookingSlots = showBooking ? Math.ceil(showBooking.duration / 15) : 0;

        return (
          <div
            key={index}
            className={`flex gap-4 ${slot.isHourMark ? 'border-t border-gray-200' : 'border-t border-gray-100'}`}
            style={{ height: '40px' }}
          >
            {/* Time label column */}
            <div className="w-24 flex-shrink-0 pt-1">
              {slot.isHourMark && (
                <span className="text-sm font-semibold text-gray-600">
                  {slot.label}
                </span>
              )}
            </div>

            {/* Content column - light gray background for empty slots */}
            <div className="flex-1 relative bg-gray-50">
              {showBooking && (
                <button
                  onClick={() => onBookingClick(showBooking)}
                  className={`absolute inset-x-0 top-0 p-3 rounded-lg bg-white border border-gray-200 border-l-4 transition-all hover:shadow-md z-10 text-left ${
                    getStatusBorderColor(showBooking.status)
                  } ${showBooking.status === 'cancelled' ? 'opacity-60' : ''}`}
                  style={{
                    height: `${bookingSlots * 40}px`,
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className={`font-semibold text-gray-900 mb-0.5 ${showBooking.status === 'cancelled' ? 'line-through' : ''}`}>
                        {showBooking.service}
                      </h3>
                      <p className={`text-sm text-gray-700 ${showBooking.status === 'cancelled' ? 'line-through' : ''}`}>
                        with {showBooking.clientName}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {showBooking.time} • {showBooking.duration} min
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                      getStatusBadgeColor(showBooking.status)
                    }`}>
                      {showBooking.status}
                    </span>
                  </div>
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ===== BOOKING DETAIL MODAL =====
const BookingDetailModal = ({ booking, onClose, navigate }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Session Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status Badge */}
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
              getStatusBadgeColor(booking.status)
            }`}>
              {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
            </span>
          </div>

          {/* Cancellation Details (if cancelled) */}
          {booking.status === 'cancelled' && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
              <h3 className="font-semibold text-rose-900 mb-2">Cancellation Details</h3>
              <div className="space-y-1 text-sm">
                {booking.cancellationReason && (
                  <p className="text-rose-800">
                    <span className="font-medium">Reason:</span> {booking.cancellationReason}
                  </p>
                )}
                {booking.cancellationNote && (
                  <p className="text-rose-700">
                    <span className="font-medium">Note:</span> {booking.cancellationNote}
                  </p>
                )}
                <p className="text-rose-600 text-xs mt-2">
                  This information is kept for your records and AI insights
                </p>
              </div>
            </div>
          )}

          {/* Client Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Client</h3>
            <p className="text-lg font-medium text-gray-900">{booking.clientName}</p>
            <button
              onClick={() => {
                navigate(`/dashboard/clients/${booking.clientId}`);
                onClose();
              }}
              className="mt-2 text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              View Full Profile →
            </button>
          </div>

          {/* Session Info */}
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-600">Service</label>
              <p className="text-gray-900">{booking.service}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Date</label>
                <p className="text-gray-900">{new Date(booking.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Time</label>
                <p className="text-gray-900">{booking.time}</p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Duration</label>
              <p className="text-gray-900">{booking.duration} minutes</p>
            </div>
          </div>

          {/* Notes */}
          {booking.notes && (
            <div>
              <label className="text-sm font-medium text-gray-600">Notes</label>
              <p className="text-gray-700 mt-1">{booking.notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
            {booking.status !== 'cancelled' && (
              <>
                <button
                  onClick={() => {
                    navigate(`/messages?client=${booking.clientId}`);
                    onClose();
                  }}
                  className="px-5 py-2 bg-purple-600 text-white rounded-lg font-semibold text-sm hover:bg-purple-700 transition-colors"
                >
                  Message Client
                </button>
                <button
                  onClick={() => {
                    navigate(`/dashboard/clients/${booking.clientId}`);
                    onClose();
                  }}
                  className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors"
                >
                  View Client Profile
                </button>
                <button
                  onClick={() => {
                    alert('Reschedule functionality coming soon');
                  }}
                  className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors"
                >
                  Reschedule
                </button>
                <button
                  onClick={() => {
                    alert('Cancel functionality coming soon');
                  }}
                  className="px-5 py-2 border border-rose-300 text-rose-600 rounded-lg font-medium text-sm hover:bg-rose-50 transition-colors"
                >
                  Cancel Session
                </button>
              </>
            )}
            {booking.status === 'cancelled' && (
              <button
                onClick={() => {
                  navigate(`/dashboard/clients/${booking.clientId}`);
                  onClose();
                }}
                className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors"
              >
                View Client Profile
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== AVAILABILITY MANAGEMENT MODAL =====
const AvailabilityModal = ({ onClose }) => {
  const [workingDays, setWorkingDays] = useState({
    sunday: false,
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false
  });

  const [workingHours, setWorkingHours] = useState({
    start: '09:00',
    end: '17:00'
  });

  const [bufferTime, setBufferTime] = useState(15);
  const [advanceNotice, setAdvanceNotice] = useState(24);

  const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Manage Availability</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Working Days */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Working Days</h3>
            <div className="flex flex-wrap gap-2">
              {dayOrder.map(day => (
                <button
                  key={day}
                  onClick={() => setWorkingDays({ ...workingDays, [day]: !workingDays[day] })}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    workingDays[day]
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                </button>
              ))}
            </div>
          </div>

          {/* Working Hours */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Working Hours</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <input
                  type="time"
                  value={workingHours.start}
                  onChange={(e) => setWorkingHours({ ...workingHours, start: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                <input
                  type="time"
                  value={workingHours.end}
                  onChange={(e) => setWorkingHours({ ...workingHours, end: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Buffer Time */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Buffer Time Between Sessions</h3>
            <p className="text-sm text-gray-600 mb-2">Time to prepare between back-to-back sessions</p>
            <select
              value={bufferTime}
              onChange={(e) => setBufferTime(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value={0}>No buffer</option>
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>60 minutes</option>
            </select>
          </div>

          {/* Advance Notice */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Minimum Advance Notice</h3>
            <p className="text-sm text-gray-600 mb-2">How much notice do you need before a booking?</p>
            <select
              value={advanceNotice}
              onChange={(e) => setAdvanceNotice(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value={1}>1 hour</option>
              <option value={4}>4 hours</option>
              <option value={12}>12 hours</option>
              <option value={24}>24 hours</option>
              <option value={48}>48 hours</option>
              <option value={72}>3 days</option>
              <option value={168}>1 week</option>
            </select>
          </div>

          {/* Time Off */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-2">Time Off</h3>
            <p className="text-sm text-gray-600 mb-3">Block specific dates when you're unavailable</p>
            <button className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors">
              + Add Time Off
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                alert('Availability saved!');
                onClose();
              }}
              className="flex-1 px-5 py-2 bg-purple-600 text-white rounded-lg font-semibold text-sm hover:bg-purple-700 transition-colors"
            >
              Save Changes
            </button>
            <button
              onClick={onClose}
              className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== MAIN CALENDAR VIEW COMPONENT =====
const CalendarView = () => {
  const navigate = useNavigate();
  const settingsRef = useRef(null);

  // Initialize view from localStorage
  const [view, setView] = useState(() => {
    const savedView = localStorage.getItem('calendarDefaultView');
    return (savedView && ['month', 'week', 'day'].includes(savedView)) ? savedView : 'month';
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedService, setSelectedService] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [dayBookingsModal, setDayBookingsModal] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [defaultView, setDefaultView] = useState(() => {
    return localStorage.getItem('calendarDefaultView') || 'month';
  });

  // Click outside handler for settings dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettings(false);
      }
    };

    if (showSettings) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSettings]);

  // Handle day click from Month view
  const handleDayClick = (targetDate) => {
    setCurrentDate(targetDate);
    setView('day');
  };

  // Handle "+X more" click from Month view
  const handleMoreClick = (data) => {
    setDayBookingsModal(data);
  };

  // Mock bookings data with variety of durations and statuses
  const bookings = [
    {
      id: 1,
      clientName: 'Sarah Martinez',
      clientId: 'client_123',
      service: 'Tarot Reading',
      date: '2026-01-29',
      time: '2:00 PM',
      duration: 60,
      status: 'confirmed',
      notes: 'Follow-up session about career transition'
    },
    {
      id: 2,
      clientName: 'Michael Roberts',
      clientId: 'client_456',
      service: 'Energy Healing',
      date: '2026-01-29',
      time: '4:30 PM',
      duration: 90,
      status: 'confirmed',
      notes: ''
    },
    {
      id: 3,
      clientName: 'Luna Starling',
      clientId: 'client_789',
      service: 'Life Coaching',
      date: '2026-01-30',
      time: '10:00 AM',
      duration: 60,
      status: 'pending',
      notes: 'New client - first session'
    },
    {
      id: 4,
      clientName: 'James Chen',
      clientId: 'client_101',
      service: 'Tarot Reading',
      date: '2026-01-31',
      time: '3:00 PM',
      duration: 60,
      status: 'confirmed',
      notes: ''
    },
    {
      id: 5,
      clientName: 'Emma Wilson',
      clientId: 'client_505',
      service: 'Energy Healing',
      date: '2026-01-29',
      time: '11:30 AM',
      duration: 90,
      status: 'confirmed',
      notes: 'Extended session for deep work'
    },
    {
      id: 6,
      clientName: 'David Park',
      clientId: 'client_606',
      service: 'Quick Tarot',
      date: '2026-01-30',
      time: '2:15 PM',
      duration: 30,
      status: 'confirmed',
      notes: 'Express reading'
    },
    {
      id: 7,
      clientName: 'Alex Thompson',
      clientId: 'client_707',
      service: 'Tarot Reading',
      date: '2026-01-28',
      time: '1:00 PM',
      duration: 60,
      status: 'cancelled',
      cancellationReason: 'Client emergency',
      cancellationNote: 'Requested to reschedule next week',
      notes: ''
    },
    {
      id: 8,
      clientName: 'Rachel Green',
      clientId: 'client_808',
      service: 'Life Coaching',
      date: '2026-02-01',
      time: '9:00 AM',
      duration: 60,
      status: 'pending',
      notes: 'Initial consultation'
    },
    {
      id: 9,
      clientName: 'Tom Mitchell',
      clientId: 'client_909',
      service: 'Energy Healing',
      date: '2026-02-03',
      time: '2:00 PM',
      duration: 90,
      status: 'confirmed',
      notes: 'Monthly session'
    }
  ];

  // Service types for filter
  const serviceTypes = [
    { value: 'all', label: 'All Services' },
    { value: 'Tarot Reading', label: 'Tarot Reading' },
    { value: 'Energy Healing', label: 'Energy Healing' },
    { value: 'Life Coaching', label: 'Life Coaching' },
    { value: 'Quick Tarot', label: 'Quick Tarot' }
  ];

  // Filter bookings
  const filteredBookings = bookings.filter(booking =>
    selectedService === 'all' || booking.service === selectedService
  );

  // Date navigation
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(currentDate.getMonth() - 1);
    } else {
      newDate.setMonth(currentDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    const change = direction === 'prev' ? -7 : 7;
    newDate.setDate(currentDate.getDate() + change);
    setCurrentDate(newDate);
  };

  const navigateDay = (direction) => {
    const newDate = new Date(currentDate);
    const change = direction === 'prev' ? -1 : 1;
    newDate.setDate(currentDate.getDate() + change);
    setCurrentDate(newDate);
  };

  const handleNavigate = (direction) => {
    if (view === 'month') navigateMonth(direction);
    if (view === 'week') navigateWeek(direction);
    if (view === 'day') navigateDay(direction);
  };

  const formatDateHeader = () => {
    if (view === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    if (view === 'week') {
      const weekStart = new Date(currentDate);
      weekStart.setDate(currentDate.getDate() - currentDate.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    if (view === 'day') {
      return currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <span
            onClick={() => navigate('/dashboard/practitioner')}
            className="text-purple-600 hover:text-purple-700 font-medium transition-colors cursor-pointer"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/practitioner'); }}
          >
            Dashboard
          </span>
          <span className="text-gray-400" aria-hidden="true">/</span>
          <span className="text-gray-900 font-medium" aria-current="page">Calendar</span>
        </nav>

        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
              <p className="text-sm text-gray-600">Manage your bookings and availability</p>
            </div>
          </div>

          {/* Controls Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
              {/* Date Navigation */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleNavigate('prev')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h2 className="text-lg font-semibold text-gray-900 min-w-[200px] text-center">
                  {formatDateHeader()}
                </h2>
                <button
                  onClick={() => handleNavigate('next')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="ml-2 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Today
                </button>
              </div>

              {/* View Toggle & Filter */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* Service Filter */}
                <div className="relative">
                  <Filter className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <select
                    value={selectedService}
                    onChange={(e) => setSelectedService(e.target.value)}
                    className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {serviceTypes.map(service => (
                      <option key={service.value} value={service.value}>
                        {service.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Settings Dropdown */}
                <div ref={settingsRef} className="relative">
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Settings className="w-5 h-5 text-gray-600" />
                  </button>

                  {showSettings && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-20">
                      {/* Header */}
                      <div className="px-4 py-3 border-b border-gray-200">
                        <h3 className="font-semibold text-gray-900">Calendar Settings</h3>
                      </div>

                      {/* Default View Selection */}
                      <div className="p-4 border-b border-gray-200">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Default View
                        </label>
                        <div className="space-y-1">
                          {['month', 'week', 'day'].map(viewOption => (
                            <button
                              key={viewOption}
                              onClick={() => {
                                setDefaultView(viewOption);
                                localStorage.setItem('calendarDefaultView', viewOption);
                                setView(viewOption);
                              }}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                defaultView === viewOption
                                  ? 'bg-purple-50 text-purple-700 font-semibold'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {viewOption.charAt(0).toUpperCase() + viewOption.slice(1)} View
                              {defaultView === viewOption && (
                                <span className="float-right text-purple-600">✓</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Manage Availability */}
                      <div className="p-2">
                        <button
                          onClick={() => {
                            setShowAvailabilityModal(true);
                            setShowSettings(false);
                          }}
                          className="w-full text-left px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <span className="font-medium">Manage Availability</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* View Selector */}
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  {['month', 'week', 'day'].map(viewType => (
                    <button
                      key={viewType}
                      onClick={() => setView(viewType)}
                      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        view === viewType
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {viewType.charAt(0).toUpperCase() + viewType.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar Display */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {view === 'month' && (
            <MonthView
              bookings={filteredBookings}
              currentDate={currentDate}
              onBookingClick={setSelectedBooking}
              onDayClick={handleDayClick}
              onMoreClick={handleMoreClick}
            />
          )}
          {view === 'week' && <WeekView bookings={filteredBookings} currentDate={currentDate} onBookingClick={setSelectedBooking} />}
          {view === 'day' && <DayView bookings={filteredBookings} currentDate={currentDate} onBookingClick={setSelectedBooking} />}
        </div>

        {/* Status Legend */}
        <div className="flex items-center justify-center gap-6 mt-4 pb-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
            <span className="text-sm text-gray-700 font-medium">Confirmed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-400"></div>
            <span className="text-sm text-gray-700 font-medium">Pending</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-rose-500"></div>
            <span className="text-sm text-gray-700 font-medium">Cancelled</span>
          </div>
        </div>

        {/* Booking Detail Modal */}
        {selectedBooking && (
          <BookingDetailModal
            booking={selectedBooking}
            onClose={() => setSelectedBooking(null)}
            navigate={navigate}
          />
        )}

        {/* Availability Management Modal */}
        {showAvailabilityModal && (
          <AvailabilityModal
            onClose={() => setShowAvailabilityModal(false)}
          />
        )}

        {/* Day Bookings Modal (for "+X more" button) */}
        {dayBookingsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">
                  {dayBookingsModal.date.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric'
                  })}
                </h3>
                <button
                  onClick={() => setDayBookingsModal(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="p-6 space-y-3">
                {dayBookingsModal.bookings.map(booking => (
                  <button
                    key={booking.id}
                    onClick={() => {
                      setDayBookingsModal(null);
                      setSelectedBooking(booking);
                    }}
                    className={`w-full text-left p-4 rounded-lg bg-white border border-gray-200 border-l-4 transition-all hover:shadow-md ${
                      getStatusBorderColor(booking.status)
                    } ${booking.status === 'cancelled' ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className={`font-semibold text-gray-900 ${booking.status === 'cancelled' ? 'line-through' : ''}`}>
                          {booking.service}
                        </div>
                        <div className={`text-sm text-gray-700 ${booking.status === 'cancelled' ? 'line-through' : ''}`}>
                          {booking.clientName}
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        getStatusBadgeColor(booking.status)
                      }`}>
                        {booking.status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {booking.time} • {booking.duration} minutes
                    </div>
                  </button>
                ))}
              </div>

              <div className="border-t border-gray-200 px-6 py-4">
                <button
                  onClick={() => {
                    setCurrentDate(dayBookingsModal.date);
                    setView('day');
                    setDayBookingsModal(null);
                  }}
                  className="w-full px-5 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                >
                  View Full Day
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarView;
